export const trFormSchema = {
  api: {
    // fetch can be added later if needed
    submit: 'trip-reports'
  },
  payload: {
    includeRawFormData: true,
    // Map schema field names to API keys as needed
    map: {
      arrivalDate: 'fromDate',
      departureDate: 'toDate'
    },
    // Transform form-backed contact selection ids into API traveler ids
    // (mirrors TAR's pattern: store contactId in UI, send travelerId to API)
    transform: (data) => {
      const transformed = { ...data };
      const travelerId = data?.contactId || data?.travelerId;
      const pocTravelerId = data?.pocContactId || data?.pocTravelerId;

      if (travelerId) transformed.travelerId = travelerId;
      if (pocTravelerId) transformed.pocTravelerId = pocTravelerId;

      // Don't send UI-only fields if backend expects traveler ids
      delete transformed.contactId;
      delete transformed.pocContactId;

      return transformed;
    },
    // Ensure numeric fields are cast correctly
    casts: {
      formVersionNumber: 'int',
      tarVersionNumber: 'int',
      totalCostOfTrip: 'float'
    },
    // Optional: allow-list to control payload shape strictly
    allow: [
      'awardPIID',
      'commentsConclusions',
      'formId',
      'formVersionNumber',
      'fromDate',
      'fromLocationOfTravel',
      'knowledgeGained',
      'projectId',
      'projectName',
      'purposeJustification',
      'rawFormData',
      'tarId',
      'tarVersionNumber',
      'toDate',
      'toLocationOfTravel',
      'totalCostOfTrip',
      'trDate',
      'trNumber',
      'travelerId',
      'pocTravelerId',
      'description',
      'dueDate',
    ]
  },
  blocks: [
    // Hidden read-only values fetched from forms API (not rendered)
    {
      type: 'hiddenApi',
      route: 'forms',
      // Find the TR form record by formType
      filter: { formType: 'Trip Report (TR) Form' },
      map: {
        formId: 'formId',
        formVersionNumber: 'versionNumber',
      },
    },
    { type: 'heading', label: 'Trip Report (TR)', divider: false },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'trNumber', label: 'TR Number', placeholder: 'TR-2025-001', required: true, flex: '1 1 300px' },
        { type: 'date', name: 'dueDate', label: 'Due Date', required: false, payloadTransform: (v) => (v && v !== '') ? v : undefined, flex: '0 0 200px' },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'description', label: 'Description', required: false, multiline: true, minRows: 3 },
      ],
    },
    {
      type: 'group',
      fields: [
        { type: 'date', name: 'trDate', label: 'Trip Report Date', required: true },
      ],
      note:
        'Trip Report shall be completed within 10 workdays following completion of each trip (unless specified otherwise in Section F).',
    },
    // TAR Number - fetched from tars API with client-side filtering
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'tarId',
          label: 'Travel Authorization Number (TAR)',
          required: true,
          placeholder: 'Search approved TAR',
          loadFromApiConfig: {
            url: 'api/tars',
            params: {
              currentVersion: true
            }
          },
          loadFromApiField: 'tars',
          optionLabelKey: 'tarNumber',
          optionValueKey: 'tarId',
          // Client-side filter: only show TARs with workflowState 'Federally Approved'
          filterBy: {
            field: 'workflowState',
            value: 'Federally Approved',
            matchType: 'equals'
          },
          mapFields: {
            tarVersionNumber: 'versionNumber',
            projectId: 'projectId',
            programId: 'programId',
            // Ensure the display name is stored and included in payload as projectName
            projectName: ['projectName', 'project.title', 'projectNameLabel'],
          },
          helperText: 'Only federally approved TARs are shown'
        }
      ]
    },
    // Project Name (full width) - auto-populated from TAR selection, not directly editable
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'projectId',
          label: 'Project Name',
          required: true,
          placeholder: 'Auto-filled from TAR',
          loadFromApiRoute: 'projects',
          loadFromApiField: 'projects',
          optionLabelKey: 'title',
          optionValueKey: 'projectId',
          mapFields: { projectName: 'title' },
          readOnly: true,
          helperText: 'Autofilled based on TAR number'
        },
      ],
    },
    // Award PIID Number (full width)
    {
      type: 'group',
      fields: [
        { type: 'text', name: 'awardPIID', label: 'Award PIID Number', required: true, placeholder: 'Award PIID Number' }
      ]
    },
    // Name of Traveler (full width)
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'contactId',
          label: 'Name of Traveler',
          required: true,
          placeholder: 'First & Last',
          loadFromApiRoute: 'contacts',
          optionLabelKey: 'name',
          optionValueKey: 'contactId',
          mapFields: { travelerName: 'name' },
          helperText: 'First & Last'
        }
      ],
    },
    // From / To (two columns) with helper text "Location of Travel"
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'fromLocationOfTravel',
          label: 'From',
          placeholder: 'Search location',
          required: true,
          loadFromApiRoute: 'locations',
          optionLabelKey: 'name',
          optionValueKey: 'locationId',
          mapFields: {
            fromLocationName: 'name',
            fromLocationCode: 'locationCode',
            fromLocationType: 'locationType',
            fromLocationDescription: 'description',
            fromLocationArchived: 'archived'
          },
          helperText: 'Location of Travel'
        },
        {
          type: 'autocomplete',
          name: 'toLocationOfTravel',
          label: 'To',
          placeholder: 'Search location',
          required: true,
          loadFromApiRoute: 'locations',
          optionLabelKey: 'name',
          optionValueKey: 'locationId',
          mapFields: {
            toLocationName: 'name',
            toLocationCode: 'locationCode',
            toLocationType: 'locationType',
            toLocationDescription: 'description',
            toLocationArchived: 'archived'
          },
          helperText: 'Location of Travel'
        },
      ],
    },
    // Arrival / Departure / Duration row
    {
      type: 'group',
      fields: [
        {
          type: 'dateRange',
          name: 'travelDates',
          label: 'Travel Dates',
          required: true,
          // Map range to fromDate/toDate in payload
          payloadRangeKeys: { start: 'fromDate', end: 'toDate' },
        },
        { type: 'text', name: 'durationDays', label: 'Duration', required: false, placeholder: '0', readOnly: true, helperText: 'Days' },
      ],
    },
    // POC (full width) with helper text First & Last
    {
      type: 'group',
      fields: [
        {
          type: 'autocomplete',
          name: 'pocContactId',
          label: 'Name of Point of Contact (POC) at Location',
          required: true,
          placeholder: 'First & Last',
          // Use contacts (same data source as Traveler)
          loadFromApiRoute: 'contacts',
          optionLabelKey: 'name',
          optionValueKey: 'contactId',
          mapFields: { pocTravelerName: 'name' },
          helperText: 'First & Last'
        }
      ],
    },
    { type: 'group', fields: [ { type: 'text', name: 'totalCostOfTrip', label: 'Total Cost of Trip', required: true, placeholder: '0.00', dollars: true } ] },
    {
      type: 'group',
      fields: [
        {
          type: 'text',
          name: 'purposeJustification',
          label: 'Purpose of Trip',
          required: true,
          multiline: true,
          placeholder: '',
        },
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'text',
          name: 'knowledgeGained',
          label: 'Knowledge Gained',
          required: true,
          multiline: true,
          placeholder: '',
          helperText:
            'Provide a detailed description of any knowledge gained. Attach additional pages if necessary.',
        },
      ],
    },
    {
      type: 'group',
      fields: [
        {
          type: 'text',
          name: 'commentsConclusions',
          label: 'Comments, Conclusions, Action Items',
          required: true,
          multiline: true,
          placeholder: '',
          helperText:
            'Provide any additional comments, conclusions or action items. Attach additional pages if necessary.',
        },
      ],
    },
    // File upload field for attachments
    {
      type: 'group',
      fields: [
        {
          type: 'fileUpload',
          name: 'attachments',
          label: 'Attachments',
          required: false,
          objectType: 'TR',
          helperText: 'Upload supporting documents, additional pages, or other relevant files'
        }
      ]
    },
  ]
};
